import type {
  AgentExecutionDto,
  AgentExecutionOrderByFieldType,
  CountByStatusResponseDto,
  FindByTargetQueryInputDto,
} from '@terra/shared-contracts-agent-execution';
import {
  AgentExecutionStatusSchema,
  ExecutionTypeSchema,
  type AgentExecutionStatusType,
  type ExecutionTypeType,
} from '@terra/shared-schemas-ai';
import {
  DEFAULT_LIFECYCLE_STATE_FILTER,
  type LifecycleStateFilterDto,
} from '@terra/shared-dto-types';
import type { AgentExecutionListSort } from '../../../store/tab/agentExecutionsTabScopedFilterStore';

export const AGENT_EXECUTIONS_PAGE_SIZE = 50;
export const AGENT_EXECUTIONS_FETCH_ALL_PAGE_SIZE = 500;

export const AGENT_EXECUTION_STATUS_VALUES: readonly AgentExecutionStatusType[] =
  AgentExecutionStatusSchema.options;

export const AGENT_EXECUTION_TYPE_VALUES: readonly ExecutionTypeType[] =
  ExecutionTypeSchema.options;

const SORTABLE_COLUMNS = new Set<string>(['createdAt', 'status', 'duration']);

export const isAgentExecutionsSortableColumn = (
  colId: string,
): colId is AgentExecutionOrderByFieldType => SORTABLE_COLUMNS.has(colId);

const ABORTABLE_STATUSES = new Set<AgentExecutionStatusType>([
  'PENDING',
  'RUNNING',
  'BLOCKED',
]);

export const isAbortableAgentExecution = (
  execution: Pick<AgentExecutionDto, 'status'>,
) => ABORTABLE_STATUSES.has(execution.status);

export const toArchivedQuery = (
  lifecycleState: LifecycleStateFilterDto | undefined,
): boolean | undefined => {
  if (lifecycleState === 'archived') {
    return true;
  }

  return lifecycleState === 'all' ? undefined : false;
};

type AgentExecutionsListFilters = {
  searchTerm: string;
  executionType: ExecutionTypeType | null;
  createdSince: Date | null;
  lifecycleState: LifecycleStateFilterDto | undefined;
};

export type AgentExecutionsListQuery = Omit<
  FindByTargetQueryInputDto,
  'skip' | 'limit' | 'scanId'
>;

export const buildAgentExecutionsCountQuery = ({
  searchTerm,
  executionType,
  createdSince,
  lifecycleState,
}: AgentExecutionsListFilters) => {
  const search = searchTerm.trim();

  return {
    archived: toArchivedQuery(lifecycleState),
    executionType: executionType ?? undefined,
    search: search === '' ? undefined : search,
    startDate: createdSince ?? undefined,
  };
};

export const buildAgentExecutionsListQuery = ({
  statusFilter,
  sort,
  ...filters
}: AgentExecutionsListFilters & {
  statusFilter: readonly AgentExecutionStatusType[];
  sort: AgentExecutionListSort;
}): AgentExecutionsListQuery => ({
  ...buildAgentExecutionsCountQuery(filters),
  status: statusFilter.length > 0 ? [...statusFilter] : undefined,
  orderBy: sort ? [{ field: sort.colId, order: sort.sort }] : undefined,
});

export const hasAnyAgentExecutionFilters = ({
  searchTerm,
  statusFilter,
  executionType,
  createdSince,
  selectableLifecycleState,
}: {
  searchTerm: string;
  statusFilter: readonly AgentExecutionStatusType[];
  executionType: ExecutionTypeType | null;
  createdSince: Date | null;
  selectableLifecycleState: LifecycleStateFilterDto | null;
}) =>
  searchTerm.trim() !== '' ||
  statusFilter.length > 0 ||
  executionType !== null ||
  createdSince !== null ||
  (selectableLifecycleState !== null &&
    selectableLifecycleState !== DEFAULT_LIFECYCLE_STATE_FILTER);

export const AGENT_EXECUTIONS_STATUS_CHIP_ALL = 'ALL';

export type AgentExecutionsStatusChip =
  | typeof AGENT_EXECUTIONS_STATUS_CHIP_ALL
  | AgentExecutionStatusType;

export const AGENT_EXECUTIONS_STATUS_CHIPS: readonly AgentExecutionsStatusChip[] =
  [AGENT_EXECUTIONS_STATUS_CHIP_ALL, ...AGENT_EXECUTION_STATUS_VALUES];

export const resolveAgentExecutionsStatusChip = (
  statusFilter: readonly AgentExecutionStatusType[],
): AgentExecutionsStatusChip | null => {
  if (statusFilter.length === 0) {
    return AGENT_EXECUTIONS_STATUS_CHIP_ALL;
  }

  const [onlyStatus] = statusFilter;

  return statusFilter.length === 1 && onlyStatus ? onlyStatus : null;
};

export const toAgentExecutionsStatusFilter = (
  chip: AgentExecutionsStatusChip,
): AgentExecutionStatusType[] =>
  chip === AGENT_EXECUTIONS_STATUS_CHIP_ALL ? [] : [chip];

export const resolveAgentExecutionsStatusChipCount = ({
  chip,
  statusCounts,
}: {
  chip: AgentExecutionsStatusChip;
  statusCounts: Partial<CountByStatusResponseDto> | null;
}): number | undefined => {
  if (!statusCounts) {
    return undefined;
  }

  if (chip !== AGENT_EXECUTIONS_STATUS_CHIP_ALL) {
    return statusCounts[chip] ?? 0;
  }

  return AGENT_EXECUTION_STATUS_VALUES.reduce(
    (total, status) => total + (statusCounts[status] ?? 0),
    0,
  );
};

export const resolveNextAgentExecutionsPageSkip = ({
  pages,
}: {
  pages: ReadonlyArray<{
    executions: readonly unknown[];
    totalCount: number;
  }>;
}): number | undefined => {
  const lastPage = pages.at(-1);
  const loadedCount = pages.reduce(
    (sum, page) => sum + page.executions.length,
    0,
  );

  return lastPage &&
    lastPage.executions.length > 0 &&
    loadedCount < lastPage.totalCount
    ? loadedCount
    : undefined;
};

export const resolveAgentExecutionTitle = (
  execution: Pick<
    AgentExecutionDto,
    'objective' | 'craftDescription' | 'craftName'
  >,
): string | null =>
  execution.objective?.trim() ||
  execution.craftDescription?.trim() ||
  execution.craftName?.trim() ||
  null;

export const formatExecutionDuration = (
  durationMs: number | null | undefined,
): string | null => {
  if (!durationMs) {
    return null;
  }

  const totalSeconds = Math.round(durationMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
};

export const chunkIds = ({
  ids,
  size,
}: {
  ids: readonly string[];
  size: number;
}): string[][] =>
  Array.from({ length: Math.ceil(ids.length / size) }, (_, index) =>
    ids.slice(index * size, (index + 1) * size),
  );
